import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { ArrowLeftIcon, SendIcon } from 'lucide-react';
import { Fragment } from 'react';

import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { regions } from 'src/shared/entities';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const amazonS3FormSchema = z.object({
  accountName: z.string().min(1, 'Enter a name for this integration'),
  region: z.string({ message: 'Enter your AWS region' }).min(1, 'Enter your AWS region'),
  username: z.string().min(1, 'Enter your access key ID'),
  password: z.string().min(1, 'Enter your secret key'),
});

export type IAmazonS3Form = z.infer<typeof amazonS3FormSchema>;

type Props = {
  onSubmit: (data: IAmazonS3Form) => Promise<void>;
  onBack: () => void;
  accountName?: string;
  btnText?: React.ReactNode;
};

const AmazonS3Form = ({ onSubmit, onBack, accountName = '', btnText }: Props) => {
  const form = useForm<IAmazonS3Form>({
    resolver: zodResolver(amazonS3FormSchema),
    defaultValues: {
      accountName: accountName || '',
      region: undefined,
      username: '',
      password: '',
    },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-4">
          <FormField
            control={form.control}
            name="accountName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Integration name</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="e.g., My Amazon S3 Org name" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="region"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Region</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="e.g., us-east-1" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {regions.map(({ region, name }) => (
                      <SelectItem value={region} key={region}>
                        {name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="username"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Access key ID</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Paste your access key ID here" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Secret access key</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Paste your secret access key here" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
          <Button size="sm" variant="outline" className="sm:flex-1" onClick={onBack}>
            <ArrowLeftIcon /> View instructions
          </Button>
          <Button
            type="submit"
            size="sm"
            isLoading={form.formState.isSubmitting}
            disabled={form.formState.isSubmitting}
            className="sm:flex-1"
          >
            {btnText ?? (
              <Fragment>
                Submit <SendIcon />
              </Fragment>
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default AmazonS3Form;
